import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Trash2, Search, Box, Plus, X, Check, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { StockLocationBar } from '../../components/StockLocationBar';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { SideDrawer } from '../../components/SideDrawer';
import {
  resolveStockScopes, effectiveBranchesOf, CENTRAL_LOCATION,
  stockRowsOf, findCatalogProduct, variantLabelOf,
  commitStockRow, deleteStockRow, addStockRow, updateStockRow, catalogProductsOf,
  stockTransfersOf, updateStockTransfer, locationLabelOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { IconAction, RowActions } from '../../components/RowActions';

export const InventoryPendingTab = ({ org, showNotification, canEdit, lockedBranchId, isT0, isT1 }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [draftPrices, setDraftPrices] = useState({});
  const [draftAlert, setDraftAlert] = useState({});
  const [draftBatches, setDraftBatches] = useState({});
  const [draftExpiries, setDraftExpiries] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newStock, setNewStock] = useState({ productId: '', variantId: '', qty: '', costPrice: '', sellPrice: '', minStockThreshold: '', batchNumber: '', expiryDate: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isAdding) setErrors({});
  }, [isAdding]);

  const scopes = useMemo(() => resolveStockScopes(org.id), [org.id, dataVersion]);
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, version, dataVersion]);

  const resolveLocation = () => {
    if (scopes.inventoryScope === 'central') return CENTRAL_LOCATION;
    if (lockedBranchId) return lockedBranchId;
    return branches[0]?.id || '';
  };
  const [locationId, setLocationId] = useState(resolveLocation);
  useEffect(() => {
    setLocationId(resolveLocation());
  }, [scopes.inventoryScope, branches, lockedBranchId]);

  const rows = useMemo(() => {
    let all = stockRowsOf(org.id, locationId, 'pending').map(row => {
      const noteStr = (row.note || '').toLowerCase();
      const isTransfer = !!row.note && noteStr.startsWith('from ');
      const isEmergencyTransfer = isTransfer && noteStr.includes('(emergency)');
      const isReserved = (noteStr.includes('emerg') || noteStr.includes('emery')) && !isTransfer;
      return {
        ...row,
        isTransfer,
        isEmergencyTransfer,
        isReserved
      };
    });
    
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((r) => {
      const p = findCatalogProduct(r.productId);
      return p && (p.name || '').toLowerCase().includes(q);
    });
  }, [search, org.id, locationId, version, dataVersion]);

  const accent = org.theme.primary;

  const commit = (row) => {
    const price = Number(draftPrices[row.id] ?? row.sellPrice);
    const alertQty = Number(draftAlert[row.id] ?? row.minStockThreshold) || 0;
    if (!price || price <= 0) {
      if (showNotification) showNotification('Set a sell price before committing.');
      return;
    }
    
    const finalBatch = draftBatches[row.id] !== undefined ? draftBatches[row.id] : (row.batchNumber || '');
    const finalExpiry = draftExpiries[row.id] !== undefined ? draftExpiries[row.id] : (row.expiryDate || '');
    
    commitStockRow(row.id, price, alertQty, finalBatch, finalExpiry, row.barcode);
    
    if (showNotification) showNotification('Moved to active inventory.', 'success');
    setVersion((v) => v + 1);
  };

  const remove = (row) => {
    deleteStockRow(row.id);
    if (showNotification) showNotification('Pending item removed.', 'success');
    setVersion((v) => v + 1);
  };

  const handleAddPendingStock = () => {
    const errs = {};
    if (!newStock.productId) errs.productId = 'Please select a product.';
    
    const qtyNum = Number(newStock.qty) || 0;
    if (qtyNum <= 0) errs.qty = 'Quantity must be > 0.';
    
    if (String(newStock.minStockThreshold ?? '').trim() === '' || isNaN(Number(newStock.minStockThreshold))) {
      errs.minStockThreshold = 'Alert Qty is required.';
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    const existingRows = stockRowsOf(org.id, locationId);
    const existingPending = existingRows.find(r => 
      r.status === 'pending' && 
      r.productId === newStock.productId && 
      (r.variantId || '') === (newStock.variantId || '') && 
      // Stock added here is new stock arriving, so it carries no reason. Only
      // merge with other reason-less rows: an 'Unsold product' or 'Emergency
      // product' row came back off the shelf from the Active tab and must stay
      // its own card.
      (r.note || '') === ''
    );

    if (existingPending) {
      updateStockRow(existingPending.id, { qty: Number(existingPending.qty) + qtyNum });
    } else {
      addStockRow({
        orgId: org.id,
        locationId,
        productId: newStock.productId,
        variantId: newStock.variantId || '',
        qty: qtyNum,
        costPrice: Number(newStock.costPrice) || 0,
        sellPrice: Number(newStock.sellPrice) || 0,
        minStockThreshold: Number(newStock.minStockThreshold) || 0,
        batchNumber: newStock.batchNumber || '',
        expiryDate: newStock.expiryDate || '',
        status: 'pending',
        // No reason: the Reason dropdown belongs to the Active -> Pending
        // transfer in InventoryActiveTab, not to adding new stock.
        note: ''
      });
    }
    if (showNotification) showNotification('Added to pending inventory.', 'success');
    setIsAdding(false);
    setNewStock({ productId: '', variantId: '', qty: '', costPrice: '', sellPrice: '', minStockThreshold: '', batchNumber: '', expiryDate: '' });
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pending items…"
                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
            </div>

            <div className="shrink-0">
              <StockLocationBar
                org={org}
                scope={scopes.inventoryScope}
                mode={scopes.mode}
                branches={branches}
                locationId={locationId}
                onChange={setLocationId}
                accent={accent}
                lockedBranchId={lockedBranchId}
              />
            </div>
          </div>

          {canEdit && (isT0 || isT1) && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-200 shadow-sm shrink-0"
              style={{ background: accent }}
            >
              <Plus size={14} aria-hidden="true" /> Add pending stock
            </button>
          )}
        </div>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)]">
              <tr>
                <th className="px-5 py-3 whitespace-nowrap">Prod</th>
                <th className="px-3 py-3 whitespace-nowrap">Details</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Cost</th>
                <th className="px-3 py-3 whitespace-nowrap">Batch</th>
                <th className="px-3 py-3 whitespace-nowrap">Exp Date</th>
                <th className="px-3 py-3 whitespace-nowrap">Updated</th>
                <th className="px-3 py-3 whitespace-nowrap">Sell (₹)</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Alt</th>
                {canEdit && <th className="px-5 py-3 whitespace-nowrap text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {rows.map((row) => {
                const product = findCatalogProduct(row.productId);
                const isEmerg = row.isEmergencyTransfer;
                return (
                  <tr key={row.id} className={`transition-colors ${
                    row.isReserved ? 'bg-orange-50/40 hover:bg-orange-50' :
                    isEmerg ? 'bg-emerald-50/40 hover:bg-emerald-50' :
                    row.isTransfer ? 'bg-emerald-50/40 hover:bg-emerald-50' : 'hover:bg-slate-50 group'
                  }`}>
                    <td className="px-5 py-4 text-[13px] font-semibold text-[#0d0d0d]">
                      {product?.name || row.productId}
                    </td>
                    <td className="px-3 py-4 text-[11px] text-slate-500">
                      <div>{variantLabelOf(product, row.variantId)} · {row.qty} items</div>
                      {row.note && (
                        <div className={`mt-1 font-semibold uppercase tracking-wider ${row.isReserved ? 'text-orange-500' : isEmerg ? 'text-emerald-600' : 'text-orange-500'}`}>{row.note}</div>
                      )}
                    </td>
                    <td className="px-3 py-4 text-[12px] text-right text-slate-700 font-bold whitespace-nowrap">
                      ₹{row.costPrice.toFixed(2)}
                    </td>
                    <td className="px-3 py-4 text-[12px] font-mono font-medium text-amber-600 whitespace-nowrap">
                      <input
                        type="text"
                        value={draftBatches[row.id] ?? (row.batchNumber || '')}
                        onChange={(e) => setDraftBatches((d) => ({ ...d, [row.id]: e.target.value }))}
                        disabled={row.isReserved || !canEdit || (!!row.note && !row.isTransfer)}
                        placeholder="e.g. BATCH-01"
                        className="w-28 rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1.5 text-[11px] font-mono font-semibold text-[#0d0d0d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-3 py-4 text-[11px] font-medium text-slate-700 whitespace-nowrap">
                      <input
                        type="date"
                        value={draftExpiries[row.id] ?? (row.expiryDate || '')}
                        onChange={(e) => setDraftExpiries((d) => ({ ...d, [row.id]: e.target.value }))}
                        disabled={row.isReserved || !canEdit || (!!row.note && !row.isTransfer)}
                        className="w-28 rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1.5 text-[11px] font-semibold text-[#0d0d0d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-3 py-4 text-[11px] text-slate-500 font-medium whitespace-nowrap">
                      {new Date(row.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="number" step="0.01" value={draftPrices[row.id] ?? row.sellPrice ?? 0}
                        onChange={(e) => setDraftPrices((d) => ({ ...d, [row.id]: e.target.value }))}
                        disabled={row.isReserved || !canEdit || (!!row.note && !row.isTransfer)}
                        className="w-24 rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1.5 text-[11px] font-bold text-[#0d0d0d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" 
                      />
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="number" min="0" value={draftAlert[row.id] ?? row.minStockThreshold ?? 0}
                        onChange={(e) => setDraftAlert((d) => ({ ...d, [row.id]: e.target.value }))}
                        disabled={row.isReserved || !canEdit || (!!row.note && !row.isTransfer)}
                        className="w-20 rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1.5 text-[11px] font-bold text-[#0d0d0d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" 
                      />
                    </td>
                    {canEdit && (
                      <td className="px-5 py-4 text-right">
                        <RowActions>
                          <IconAction icon={Trash2} tone="danger" label={`Drop ${row.name || 'this line'}`} title="Drop" onClick={() => remove(row)} />
                          {!row.isReserved && (
                            <IconAction icon={Check} tone="success" label={`Commit ${row.name || 'this line'} to active stock`} title="Commit" onClick={() => commit(row)} />
                          )}
                        </RowActions>
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 7 : 6} className="px-5 py-12 text-center text-slate-400">
                    <Box size={24} className="mx-auto mb-2 opacity-20" />
                    <p className="text-xs font-semibold uppercase tracking-wider">No pending stock found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {isAdding && (
        <SideDrawer onClose={() => setIsAdding(false)}>
          <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <h3 className="text-base font-extrabold text-[#0d0d0d]">Add pending stock</h3>
            <button type="button" onClick={() => setIsAdding(false)} className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50" aria-label="Close"><X size={18} /></button>
          </header>
          <div className="px-5 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Product</span>
                <CustomSelect
                  value={newStock.productId}
                  onChange={(v) => {
                    const p = products.find(prod => prod.id === v);
                    let autoExp = '';
                    if (p && Number(p.shelfLifeDays) > 0) {
                      const d = new Date();
                      d.setDate(d.getDate() + Number(p.shelfLifeDays));
                      autoExp = d.toISOString().slice(0, 10);
                    }
                    setNewStock({ 
                      ...newStock, 
                      productId: v, 
                      variantId: '',
                      costPrice: p?.basePrice ?? '',
                      expiryDate: autoExp,
                    });
                    setErrors({ ...errors, productId: undefined });
                  }}
                  options={products.map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Select product"
                  className={`w-full bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 ${errors.productId ? 'border border-rose-400' : 'border-gray-100'}`}
                />
                {errors.productId && <p className="text-[11px] font-medium text-rose-500">{errors.productId}</p>}
              </label>

              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Variant</span>
                <CustomSelect
                  value={newStock.variantId}
                  onChange={(v) => {
                    const p = products.find(prod => prod.id === newStock.productId);
                    const vr = p?.variants?.find(varnt => varnt.id === v);
                    setNewStock({ 
                      ...newStock, 
                      variantId: v,
                      costPrice: vr?.price ?? p?.basePrice ?? newStock.costPrice
                    });
                  }}
                  options={
                    newStock.productId && products.find((p) => p.id === newStock.productId)?.variants?.length > 0
                      ? [
                          { value: '', label: 'Standard' },
                          ...products.find((p) => p.id === newStock.productId).variants.map((v) => ({ value: v.id, label: v.label }))
                        ]
                      : [{ value: '', label: 'Standard' }]
                  }
                  disabled={!newStock.productId || !(products.find((p) => p.id === newStock.productId)?.variants?.length > 0)}
                  className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Qty</span>
                <input type="number" value={newStock.qty} onChange={(e) => { setNewStock({ ...newStock, qty: e.target.value }); setErrors({ ...errors, qty: undefined }); }} placeholder="e.g. 10" className={`w-full rounded-lg bg-gray-50 border focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] placeholder:text-slate-300 ${errors.qty ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'}`} />
                {errors.qty && <p className="text-[11px] font-medium text-rose-500">{errors.qty}</p>}
              </label>
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Alert Qty *</span>
                <input type="number" min="0" value={newStock.minStockThreshold} onChange={(e) => { setNewStock({ ...newStock, minStockThreshold: e.target.value }); setErrors({ ...errors, minStockThreshold: undefined }); }} placeholder="e.g. 5" className={`w-full rounded-lg bg-gray-50 border focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] placeholder:text-slate-300 ${errors.minStockThreshold ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'}`} />
                {errors.minStockThreshold && <p className="text-[11px] font-medium text-rose-500">{errors.minStockThreshold}</p>}
              </label>
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Buying Price (₹)</span>
                <input type="number" step="0.01" value={newStock.costPrice} onChange={(e) => setNewStock({ ...newStock, costPrice: e.target.value })} placeholder="e.g. 100.00" className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] placeholder:text-slate-300" />
              </label>
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Selling Price (₹)</span>
                <input type="number" step="0.01" value={newStock.sellPrice} onChange={(e) => setNewStock({ ...newStock, sellPrice: e.target.value })} placeholder="e.g. 150.00" className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] placeholder:text-slate-300" />
              </label>
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Batch Number</span>
                <input type="text" value={newStock.batchNumber || ''} onChange={(e) => setNewStock({ ...newStock, batchNumber: e.target.value })} placeholder="e.g. BATCH-01" className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-mono font-medium text-[#0d0d0d] placeholder:text-slate-300" />
              </label>
              <label className="block space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Expiry Date</span>
                  {Number(products.find(p => p.id === newStock.productId)?.shelfLifeDays) > 0 && (
                    <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 rounded border border-emerald-200">
                      Shelf Life: {products.find(p => p.id === newStock.productId).shelfLifeDays}d
                    </span>
                  )}
                </div>
                <CustomDatePickerFixed value={newStock.expiryDate || ''} onChange={(val) => setNewStock({ ...newStock, expiryDate: val })} />
              </label>
            </div>
          </div>
          <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
            <button type="button" onClick={handleAddPendingStock} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>Add to pending</button>
          </footer>
        </SideDrawer>
      )}
    </div>
  );
};

InventoryPendingTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  canEdit: PropTypes.bool,
  lockedBranchId: PropTypes.string,
  isT0: PropTypes.bool,
  isT1: PropTypes.bool,
};
